<template>
<div id="stats">
    <header>
        <a href="#" @click.prevent="goBack" class="back"></a>
        <h1>Statistik</h1>
    </header>

    <section>
        <div class="item">
            <dp :who="data.user" />
            <h3>{{ data.user | name }}</h3>
        </div>
    </section>

    <section>
        <header>
            <h2>
                ELO: {{ data.user.elo }} 
                <template v-if="eloChange > 0">
                    ({{ eloChange }} ⬆️)
                </template>
                <template v-if="eloChange < 0">
                    ({{ eloChange }} ⬇️)
                </template>
                <template v-if="eloChange == 0">
                    (±0)
                </template>
            </h2>
            <toggle :choices="{ 'day': 'T', 'week': 'W', 'month': 'M', 'year': 'Y' }"
                v-model="eloTimeframe" />
        </header>
        <elo-chart :matches="matchesInTimeframe" :userId="userId" :startTime="+startTime" :endTime="+new Date" />
    </section>

    <section>
        <header>
            <h2>Spiele</h2>
            <toggle :choices="{ 'all': 'Alle', 'free': 'Freie', 'tournaments': 'Turniere' }" 
                v-model="statFilter" />
        </header>

        <div class="stats">
            <div>
                <p>{{ stats.matches[statFilter].wins }} / {{ stats.matches[statFilter].total || 0 }}</p>
                <div class="pie-chart">
                    <pie-chart :wins="stats.matches[statFilter].wins" :losses="stats.matches[statFilter].losses" />
                </div>
                <label>Spiele gewonnen</label>
            </div>

            <div>
                <p>{{ stats.games[statFilter].wins }} / {{ stats.games[statFilter].total || 0 }}</p>
                <div class="pie-chart">
                    <pie-chart :wins="stats.games[statFilter].wins" :losses="stats.games[statFilter].losses" />
                </div>
                <label>Sätze gewonnen</label>
            </div>
        </div>
    </section>

    <section>
        <h2>Punkte</h2>

        <div class="stats">
            <div>
                <p>{{ stats.points[statFilter].points }}</p>
                <label>Insgesamt</label>
            </div>

            <div>
                <p>{{ (stats.points[statFilter].points / stats.matches[statFilter].total || 0).toLocaleString("de", {minimumFractionDigits: 1, maximumFractionDigits: 1}) }}</p>
                <label>pro Spiel</label>
            </div>

            <div>
                <p>{{ (stats.points[statFilter].points / stats.games[statFilter].total || 0).toLocaleString("de", {minimumFractionDigits: 1, maximumFractionDigits: 1}) }}</p>
                <label>pro Satz</label>
            </div>
        </div>
    </section>

    <section>
        <h2>Ranglisten</h2>

        <div class="stats">
            <router-link :to="`/users/${userId}/ranking/global`">
                <p>{{ data.ranks.global }}</p>
                <label>Weltweit</label>
            </router-link>

            <router-link :to="`/users/${userId}/ranking/city`" v-if="data.user.city">
                <p>{{ data.ranks.city }}</p>
                <label>{{ data.user.city.name }}</label>
            </router-link>

            <router-link :to="`/users/${userId}/ranking/friends`">
                <p>{{ data.ranks.friends }}</p>
                <label>Freunde</label>
            </router-link>

            <router-link :to="`/users/${userId}/ranking/year`" v-if="data.user.year">
                <p>{{ data.ranks.year }}</p>
                <label>{{ data.user.year | grade }}. Klasse</label>
            </router-link>

            <router-link :to="`/users/${userId}/ranking/school`" v-if="data.user.school">
                <p>{{ data.ranks.school }}</p>
                <label>{{ data.user.school.name }}</label>
            </router-link>
        </div>
    </section>

    <section v-if="data.awards.length > 0">
        <h2>Trophäen</h2>
        <div v-for="award in data.awards" class="item">
            <div :class="`award-img award-${award.type}`"></div>
            <h3>{{ award.tournament.name }}</h3>
            <small>{{ award.type | award_type }}</small>
        </div>
    </section>

    <section v-if="matches.length > 0">
        <header>
            <h2>Abgeschlossene Spiele</h2>
            <toggle :choices="{ 'all': 'Alle', 'free': 'Freie', 'tournaments': 'Turniere' }" 
                v-model="matchFilter" />
        </header>

        <router-link v-for="req in matches"
            :key="req.id"
            v-if="req.status == 'completed' && matchFilter != 'tournaments' && (matchFilter == 'all' || req.tournament_id === null)" 
            :to="'/match/' + req.id"
            class="item">
            <dp :who="req.player_a_id == userId ? req.player_b : req.player_a" />
            <h3 v-if="req.player_a_id == userId">
                {{ req.player_b.firstname + ' ' + req.player_b.lastname }}
            </h3>
            <h3 v-else>
                {{ req.player_a.firstname + ' ' + req.player_a.lastname }}
            </h3>
            <p v-if="req.player_a_id == userId">
                {{ req.points_a }}:{{ req.points_b }} -
                ELO {{ req.elo_a_delta | plusminus }}
            </p>
            <p v-else>
                {{ req.points_b }}:{{ req.points_a }} -
                ELO {{ req.elo_b_delta | plusminus }}
            </p>
            <small v-if="req.tournament">
                Teil eines Turniers
                <template v-if="req.type != 'regular'">
                    (Ausscheidungsphase)
                </template>
            </small>
        </router-link>

        <div v-if="matchFilter == 'tournaments'"
            v-for="t in tournaments"
            :key="t.tournament.id"
            class="item tournament">
            <a href="#" @click.prevent="expandTournament(t)">
                <img src="images/tournament-dark.png" class="tournament">
                <h3>{{ t.tournament.name }}</h3>
                <p>ELO {{ t.eloDelta | plusminus }}</p>
            </a>

            <div v-if="expandedTournament == t.tournament.id">
                <router-link v-for="req in t.matches"
                    :key="req.id"
                    v-if="req.status == 'completed'" 
                    :to="'/match/' + req.id"
                    class="item">
                    <dp :who="req.player_a_id == userId ? req.player_b : req.player_a" />
                    <h3 v-if="req.player_a_id == userId">
                        {{ req.player_b.firstname + ' ' + req.player_b.lastname }}
                    </h3>
                    <h3 v-else>
                        {{ req.player_a.firstname + ' ' + req.player_a.lastname }}
                    </h3>
                    <p v-if="req.player_a_id == userId">
                        {{ req.points_a }}:{{ req.points_b }} -
                        ELO {{ req.elo_a_delta | plusminus }}
                    </p>
                    <p v-else>
                        {{ req.points_b }}:{{ req.points_a }} -
                        ELO {{ req.elo_b_delta | plusminus }}
                    </p>
                    <small>
                        Teil eines Turniers
                        <template v-if="req.type != 'regular'">
                            (Ausscheidungsphase)
                        </template>
                    </small>
                </router-link>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import _ from 'underscore'
import base from 'base.vue'
import dp from 'components/dp.vue'
import piechart from 'components/pie-chart.vue'
import elochart from 'components/elo-chart.vue'
import toggle from 'components/toggle.vue'

export default {
    mixins: [ base ],
    components: { 
        'dp': dp, 
        'pie-chart': piechart, 
        'elo-chart': elochart,
        'toggle': toggle 
    },
    data () {
        const dummy = {
            free: { wins: 0, losses: 0 },
            tournaments: { wins: 0, losses: 0 }
        }

        return {
            data: {
                ranks: {},
                awards: [],
                matches: dummy,
                games: dummy,
                points: {
                    tournaments: {},
                    free: {}
                },
                user: {}
            },
            matches: [],
            statFilter: 'all',
            matchFilter: 'all',
            expandedTournament: -1,
            eloTimeframe: 'year'
        }
    },
    computed: {
        userId() {
            if (this.$route.params.id == 'me') {
                return this.user.id
            } else {
                return parseInt(this.$route.params.id)
            }
        },
        startTime () {
            const durations = {
                'day': 24*60*60*1000,
                'week': 7*24*60*60*1000,
                'month': 30*24*60*60*1000,
                'year': 365*24*60*60*1000
            }

            return new Date(new Date - durations[this.eloTimeframe])
        },
        matchesInTimeframe () {
            return _.filter(this.matches, m => new Date(m.updated_at.replace(' ', 'T') + 'Z') >= this.startTime)
        },
        eloChange () {
            if (this.matchesInTimeframe.length == 0) {
                return 0
            }

            if ((new Date(this.data.user.created_at)) >= this.startTime) {
                return this.data.user.elo - 630
            }

            const match = _.last(this.matchesInTimeframe)
            const eloAtStart = match.player_a_id == this.userId ? match.elo_a : match.elo_b
            return this.data.user.elo - eloAtStart
        },
        stats () {
            const stats = {
                matches: this.data.matches,
                games: this.data.games
            }

            _.each(stats, statItem => {
                if (!statItem) return

                _.each(statItem, subItem => {
                    subItem.total = subItem.wins + subItem.losses
                })

                statItem.all = {
                    wins: _.reduce(statItem, (sum, subItem) => sum + subItem.wins, 0),
                    losses: _.reduce(statItem, (sum, subItem) => sum + subItem.losses, 0),
                    total: _.reduce(statItem, (sum, subItem) => sum + subItem.total, 0),
                }
            })

            stats.points = this.data.points
            stats.points.all = { points: this.data.points.tournaments.points + this.data.points.free.points }

            return stats
        },
        tournaments () {
            const matchesByTournament = _.groupBy(_.filter(this.matches, m => m.tournament_id !== null), m => m.tournament_id)
            const tournaments = _.map(_.values(matchesByTournament), mx => mx[0].tournament)

            return _.mapObject(tournaments, t => ({
                tournament: t,
                eloDelta: _.reduce(matchesByTournament[t.id], (elo, m) => {
                    if (m.player_a_id === this.userId) {
                        return elo + m.elo_a_delta
                    } else {
                        return elo + m.elo_b_delta
                    }
                }, 0),
                matches: matchesByTournament[t.id]
            }))
        }
    },
    methods: {
        fetchData: function() {
            const that = this
            const requests = []

            // Computer properties are not available at time of initialization
            const userId = this.$route.params.id == 'me' ?
                this.user.id :
                this.$route.params.id

            return this.$api.get(`users/${userId}/stats`)
            .then(response => {
                that.data = response.data.stats
                that.matches = response.data.matches
            })
        },
        viewScore (match) {
            router.push('/match/' + match.id)
        },
        expandTournament (t) {
            if (this.expandedTournament != t.tournament.id) {
                this.expandedTournament = t.tournament.id
            } else {
                this.expandedTournament = -1
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// stats.vue?645bb328